import React, { useState, useEffect } from 'react';
import { useFeathers } from '../../app/util';
import { OfficialsSelectInput } from '../OfficialsSelectInput';
import { CandidateNamePhoto } from '../CandidateNamePhoto';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';

interface CandidateMatchCellProps {
  official?: string | { $create: true };
  createCandidate?: {
    name: string;
    party?: string;
  };
  seedSearchText?: string;
  onChange: (value: { official?: string | { $create: true }; createCandidate?: { name: string; party?: string } }) => void;
  office?: any;
  district?: any;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  partyOptions?: Array<{ label: string; value: string }>;
  showCreateCandidateParty?: boolean;
  clearAfterSelect?: boolean;
}

const getErrorStatusCode = (error: unknown): unknown => (
  typeof error === 'object' && error !== null
    ? (error as { code?: unknown; status?: unknown }).code ?? (error as { status?: unknown }).status
    : undefined
);

const CandidateMatchCell: React.FC<CandidateMatchCellProps> = ({
  official,
  createCandidate,
  seedSearchText,
  onChange,
  office,
  district,
  disabled = false,
  placeholder,
  style,
  partyOptions = [],
  showCreateCandidateParty = true,
  clearAfterSelect = false
}) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [isEditing, setIsEditing] = useState(false);
  const [officialData, setOfficialData] = useState<any>(null);
  const [loadingOfficial, setLoadingOfficial] = useState(false);
  const [notFoundOfficialKey, setNotFoundOfficialKey] = useState<string | null>(null);

  const isCreatingNew = official && typeof official === 'object' && official.$create === true;
  const isExistingOfficial = official && typeof official === 'string';

  // Fetch official data when an existing official is selected
  useEffect(() => {
    if (isExistingOfficial && feathers) {
      const officialKey = official as string;
      console.log('Trying to fetch official data for:', officialKey);
      // Reset officialData if the official key changed
      if (officialData && officialData.key !== officialKey) {
        setOfficialData(null);
      }
      if (notFoundOfficialKey && notFoundOfficialKey !== officialKey) {
        setNotFoundOfficialKey(null);
      }
      // 404s should be terminal for this key so we do not keep re-searching.
      if (notFoundOfficialKey === officialKey) {
        return;
      }
      
      if (!officialData && !loadingOfficial) {
        setLoadingOfficial(true);
        feathers.getService('officials').get(officialKey)
          .then((data) => {
            setOfficialData(data);
            setLoadingOfficial(false);
          })
          .catch((error) => {
            const errorCode = getErrorStatusCode(error);
            if (errorCode === 404) {
              setNotFoundOfficialKey(officialKey);
              setLoadingOfficial(false);
              return;
            }
            console.error('Error fetching official:', error);
            setLoadingOfficial(false);
          });
      }
    } else {
      // Reset officialData if not an existing official
      if (officialData) {
        setOfficialData(null);
      }
      if (notFoundOfficialKey) {
        setNotFoundOfficialKey(null);
      }
    }
  }, [isExistingOfficial, official, feathers, officialData, loadingOfficial, notFoundOfficialKey]);

  // Handle OfficialsSelectInput change
  const handleOfficialChange = (event: any) => {
    const target = event.target;
    
    if (target.isNew) {
      // Creating a new candidate
      const newName = target.value?.name || target.value?.text || target.value;
      onChange({
        official: { $create: true },
        createCandidate: {
          name: newName,
          party: createCandidate?.party
        }
      });
    } else if (target.value?.key) {
      // Existing official selected
      onChange({
        official: target.value.key,
        createCandidate: undefined
      });
      setOfficialData(null); // Reset official data to trigger refetch
      setNotFoundOfficialKey(null);
    } else if (typeof target.value === 'string') {
      // Direct string value (official key)
      onChange({
        official: target.value,
        createCandidate: undefined
      });
      setOfficialData(null); // Reset official data to trigger refetch
      setNotFoundOfficialKey(null);
    }
    setIsEditing(false);
  };

  // Handle name change for create candidate
  const handleNameChange = (newName: string) => {
    if (isCreatingNew) {
      onChange({
        official: { $create: true },
        createCandidate: {
          name: newName,
          party: createCandidate?.party
        }
      });
    }
  };

  // Handle party change for create candidate
  const handlePartyChange = (newParty: string) => {
    if (isCreatingNew) {
      onChange({
        official: { $create: true },
        createCandidate: {
          name: createCandidate?.name || '',
          party: newParty
        }
      });
    }
  };

  // If nothing is set, or if createCandidate exists but official is not $create, show OfficialsSelectInput
  if ((!official && !createCandidate) || (createCandidate && !isCreatingNew)) {
    return (
      <OfficialsSelectInput
        value={null}
        onChange={handleOfficialChange}
        office={office}
        district={district}
        seedSearchText={createCandidate?.name}
        placeholder={placeholder}
        style={style}
        className=""
        clearAfterSelect={clearAfterSelect}
      />
    );
  }

  // If creating new candidate
  if (isCreatingNew) {
    const candidateName = createCandidate?.name || '';
    
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          width: '100%',
          ...style
        }}
      >
        {isEditing ? (
          <OfficialsSelectInput
            value={null}
            onChange={(event: any) => {
              const target = event.target;
              if (target.isNew) {
                const newName = target.value?.name || target.value?.text || target.value;
                handleNameChange(newName);
              } else if (target.value?.key) {
                onChange({
                  official: target.value.key,
                  createCandidate: undefined
                });
              }
              setIsEditing(false);
            }}
            office={office}
            district={district}
            seedSearchText={candidateName}
            placeholder={placeholder}
            style={style}
            className=""
            clearAfterSelect={clearAfterSelect}
          />
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="body2"
                onClick={disabled ? undefined : () => setIsEditing(true)}
                sx={{
                  cursor: disabled ? 'default' : 'pointer',
                  padding: '4px 0',
                  '&:hover': disabled ? {} : { textDecoration: 'underline' }
                }}
              >
                {candidateName || '--'}
              </Typography>
            </Box>
          )}

          <Typography
            variant="caption"
            sx={{
              padding: '4px 8px',
              borderRadius: '12px',
              color: '#1565c0',
              backgroundColor: '#e3f2fd',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}
          >
            Create as new official
          </Typography>

          {showCreateCandidateParty && (
            <TextField
              select
              size="small"
              variant="standard"
              value={createCandidate?.party || ''}
              onChange={(e) => handlePartyChange(e.target.value)}
              disabled={disabled}
              sx={{ minWidth: 40, ml: 3 }}
            >
              <MenuItem value="">None</MenuItem>
              {partyOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          )}
      </Box>
    );
  }

  // If existing official is selected
  if (isExistingOfficial) {
    if (notFoundOfficialKey === official) {
      return (
        <Typography variant="caption" color="textSecondary">
          not found
        </Typography>
      );
    }

    if (loadingOfficial) {
      return <Typography variant="body2">Loading...</Typography>;
    }

    if (isEditing) {
      return (
        <OfficialsSelectInput
          value={null}
          onChange={handleOfficialChange}
          office={office}
          district={district}
          seedSearchText={seedSearchText}
          placeholder={placeholder}
          style={style}
          className=""
          clearAfterSelect={clearAfterSelect}
        />
      );
    }

    const officialName = officialData?.recentCandidate?.stagingDraft?.name || 
                        officialData?.recentCandidate?.name || 
                        'Unknown';
    const officialPhoto = officialData?.recentCandidate?.stagingDraft?.photoPathFace ||
                          officialData?.recentCandidate?.photoPathFace;

    return (
      <Box
        onClick={disabled ? undefined : () => {
          setIsEditing(true)
        }}
        sx={{
          cursor: disabled ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          '&:hover': disabled ? {} : { opacity: 0.7 },
          ...style
        }}
      >
        <CandidateNamePhoto
          name={officialName}
          photo={officialPhoto}
          style={{}}
        />
      </Box>
    );
  }

  return null;
};

export default CandidateMatchCell;

